import { useMutation, useQueryClient } from '@tanstack/react-query'
import { LayersIcon, PlusIcon, XIcon } from 'lucide-react'
import { useState } from 'react'

import { vpcApi } from '@nebula/shared/services'
import type { VPCCreateRequest } from '@nebula/shared/services'

type CreateVPCModalProps = {
  isOpen: boolean
  onClose: () => void
  onCreated?: () => void
}

export default function CreateVPCModal({ isOpen, onClose, onCreated }: CreateVPCModalProps) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [cidr, setCidr] = useState('')

  const createVpcMutation = useMutation({
    mutationFn: (data: VPCCreateRequest) => vpcApi.createVPC(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-vpc'] })
      queryClient.invalidateQueries({ queryKey: ['customer-vpcs'] })
      queryClient.invalidateQueries({ queryKey: ['end-customer-topology'] })
      handleClose()
      onCreated?.()
    },
  })

  const handleClose = () => {
    setName('')
    setCidr('')
    createVpcMutation.reset()
    onClose()
  }

  const cidrPrefix = cidr.match(/\/(\d+)$/)?.[1]
  const cidrTooSmall = cidrPrefix ? Number.parseInt(cidrPrefix) > 30 : false

  if (!isOpen) return null

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <LayersIcon className="size-5 text-purple-600" />
              Criar Nova VPC
            </h3>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <XIcon className="size-5" />
            </button>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Uma VPC (Rede Privada Virtual) isola suas máquinas virtuais em uma rede segura.
          </p>
        </div>

        <div className="space-y-4 p-6">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Nome da VPC (opcional)
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: vpc-produção"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Se não informado, será gerado automaticamente.
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">CIDR (opcional)</label>
            <input
              type="text"
              value={cidr}
              onChange={(e) => setCidr(e.target.value)}
              placeholder="Ex: 10.100.1.0/24"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono focus:border-transparent focus:ring-2 focus:ring-purple-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Se não informado, será alocado automaticamente. Mínimo /30, recomendado /24.
            </p>
            {cidrTooSmall && (
              <p className="mt-1 text-xs text-red-600">
                /{cidrPrefix} é muito pequeno. Use no máximo /30 (mínimo 2 hosts). Recomendado: /24.
              </p>
            )}
          </div>

          {createVpcMutation.error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-800">
                {(() => {
                  const err = createVpcMutation.error as any
                  const msg =
                    err?.response?.data?.error?.message ||
                    err?.response?.data?.detail ||
                    err?.message
                  if (typeof msg === 'string' && msg.toLowerCase().includes('sdn')) {
                    return 'O sistema de rede (SDN) não está disponível neste momento. Tente novamente em alguns minutos ou entre em contato com o suporte.'
                  }
                  return msg || 'Erro ao criar VPC'
                })()}
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 border-t border-gray-200 p-6">
          <button
            onClick={handleClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={() => {
              createVpcMutation.mutate({
                vpc_name: name || undefined,
                custom_cidr: cidr || undefined,
              })
            }}
            disabled={createVpcMutation.isPending || cidrTooSmall}
            className="flex items-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {createVpcMutation.isPending ? (
              <>
                <div className="size-4 animate-spin rounded-full border-b-2 border-white" />
                <span>Criando...</span>
              </>
            ) : (
              <>
                <PlusIcon className="size-4" />
                <span>Criar VPC</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
